import { useQuery } from "@tanstack/react-query";
import {
  getAllMovies,
  getAllSeries,
  getBannerById,
  getCategories,
  getCategoryById,
  getComingSoonById,
  getEpisodesBySeasonId,
  getGenres,
  getLiveChannelById,
  getLiveChannels,
  getMoviesById,
  getSeasonById,
  getSeasonsBySeries,
  getSeriesById,
  getSubcategoryById,
  getSubcritpionById,
  getUserById,
  getVerticalSeriesById,
  getVerticalEpisodesBySeriesId,
} from "./api";

export const useCategories = () => {
  return useQuery({
    queryKey: ["categories"],
    queryFn: getCategories,
  });
};

export const QUERY_KEYS = {
  subscriptions: "subscriptions",
  movies: "movies",
  series: "series",
};

export const useBannerById = (id) => {
  return useQuery({
    queryKey: ["banner", id],
    queryFn: async () => {
      const data = await getBannerById(id);
      return data;
    },
    enabled: !!id,
  });
};

export const useCategoryById = (id) => {
  return useQuery({
    queryKey: ["category", id],
    queryFn: async () => {
      const data = await getCategoryById(id);
      return data;
    },
    enabled: !!id,
  });
};

export const useGenres = () => {
  return useQuery({
    queryKey: ["genres"],
    queryFn: getGenres,
  });
};

export const useMoviesById = (id) => {
  return useQuery({
    queryKey: ["movies", id],
    queryFn: async () => {
      const data = await getMoviesById(id);
      return data;
    },
    enabled: !!id,
  });
};

export const useSeriesById = (id) => {
  return useQuery({
    queryKey: ["series", id],
    queryFn: async () => {
      const data = await getSeriesById(id);
      return data;
    },
    enabled: !!id,
  });
};

export const useUserById = (id) => {
  return useQuery({
    queryKey: ["user", id],
    queryFn: async () => {
      const data = await getUserById(id);
      return data;
    },
    enabled: !!id,
  });
};

export const useSubcateogryById = (id) => {
  return useQuery({
    queryKey: ["subcategory", id],
    queryFn: async () => {
      const data = await getSubcategoryById(id);
      return data;
    },
    enabled: !!id,
  });
};

export const useSubcriptionById = (id) => {
  return useQuery({
    queryKey: ["subscription", id],
    queryFn: async () => {
      const data = await getSubcritpionById(id);
      return data;
    },
    enabled: !!id,
  });
};

export const useComingSoonById = (id) => {
  return useQuery({
    queryKey: ["coming-soon", id],
    queryFn: async () => {
      const data = await getComingSoonById(id);
      return data;
    },
    enabled: !!id,
  });
};

export const useSeasonsBySeries = (id) => {
  return useQuery({
    queryKey: ["seasons", id],
    queryFn: () => getSeasonsBySeries(id),
    select: (data) => data[1],
    enabled: !!id,
  });
};

export const useSeasonById = (id) => {
  return useQuery({
    queryKey: ["season", id],
    queryFn: () => getSeasonById(id),
    select: (data) => data[1],
    enabled: !!id,
  });
};

export const useEpisodesBySeasonId = (
  id,
  page = 1,
  limit = 10,
  search = "",
  isEnable
) => {
  return useQuery({
    queryKey: ["episodes", id, page, limit, search, isEnable],
    queryFn: () =>
      getEpisodesBySeasonId(id, page, limit, search, isEnable).then(
        ([success, data]) => {
          if (!success) throw new Error("Failed to fetch episodes");
          return data;
        }
      ),
    enabled: !!id,
  });
};

export const useMovies = (page = 1, limit = 200, status, type, search) => {
  return useQuery({
    queryKey: [QUERY_KEYS.movies, page, limit, status, type, search],
    queryFn: () =>
      getAllMovies(page, limit, status, type, search).then(
        ([success, data]) => {
          if (!success) throw new Error("Failed to fetch movies");
          return data;
        }
      ),
  });
};

export const useSeries = (page = 1, limit = 200, status) => {
  return useQuery({
    queryKey: [QUERY_KEYS.series, page, limit, status],
    queryFn: () =>
      getAllSeries(page, limit, status).then(([success, data]) => {
        if (!success) throw new Error("Failed to fetch series");
        return data;
      }),
  });
};

export const useVerticalSeriesById = (id) => {
  return useQuery({
    queryKey: ["vertical-series", id],
    queryFn: async () => {
      const data = await getVerticalSeriesById(id);
      return data;
    },
    enabled: !!id,
  });
};

export const useVerticalEpisodesBySeriesId = (id) => {
  return useQuery({
    queryKey: ["vertical-episodes", id],
    queryFn: () => getVerticalEpisodesBySeriesId(id),
    select: (data) => data[1],
    enabled: !!id,
  });
};

export const useLiveChannels = (page = 1, limit = 10) => {
  return useQuery({
    queryKey: ["live-channels", page, limit],
    queryFn: () =>
      getLiveChannels(page, limit).then(([success, data]) => {
        if (!success) throw new Error("Failed to fetch live channels");
        return data;
      }),
  });
};

export const useLiveChannelById = (id) => {
  return useQuery({
    queryKey: ["live-channel", id],
    queryFn: () =>
      getLiveChannelById(id).then(([success, data]) => {
        if (!success) throw new Error("Failed to fetch live channel");
        return data;
      }),
    enabled: !!id,
  });
};
